import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { Waypoint } from 'react-waypoint';
import {
	invalidateKeepInTouchScores,
	useKeepInTouchScores,
	useKeepInTouchTemplateQuery,
	useRunKeepInTouchJob,
} from '../../../../../queries/KeepInTouchAgent';
import { Button } from '../../../../components-v2/Button';
import { Skeleton } from '../../../../components-v2/Skeleton';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { JobProgress } from '../JobProgress';
import { ScoreCard } from './ScoreCard';
import { ScoreFilters, ScoreFilterState } from './ScoreFilters';
import { styleSheet } from './styles';

const PAGE_SIZE = 25;

interface ScoresViewProps {
	configId: string;
	onBack: () => void;
}

export const ScoresView: React.FC<ScoresViewProps> = ({ configId, onBack }) => {
	const [activeJobId, setActiveJobId] = React.useState<string | null>(null);
	const [filters, setFilters] = React.useState<ScoreFilterState>({ showSuppressed: false, ownerOnly: false });
	const { data: config } = useKeepInTouchTemplateQuery({ id: configId });
	const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useKeepInTouchScores({
		configId,
		pageSize: PAGE_SIZE,
		ownerOnly: filters.ownerOnly,
	});

	const runMutation = useRunKeepInTouchJob({
		onSuccess: result => {
			setActiveJobId(result.id);
			toast.success('Research started');
		},
		onError: (error: Api.IOperationResultNoValue) => {
			toast.error(error?.systemMessage || 'Failed to run configuration');
		},
	});

	const handleJobComplete = React.useCallback(() => {
		setActiveJobId(null);
		invalidateKeepInTouchScores(configId);
	}, [configId]);

	const handleFiltersChange = (newFilters: ScoreFilterState) => {
		setFilters(newFilters);
	};

	const allScores = data?.pages.flatMap(page => page.values) ?? [];
	const isRunning = !!activeJobId || runMutation.isPending;

	const handleLoadMore = () => {
		if (!isFetchingNextPage && hasNextPage) {
			fetchNextPage();
		}
	};

	return (
		<>
			<div className={css(styleSheet.header)}>
				<button className={css(styleSheet.backButton)} onClick={onBack}>
					&larr; Back
				</button>
				<h2 className={css(styleSheet.configName)}>{config?.name ?? 'Loading...'}</h2>
				<Button
					variant='outline'
					size='sm'
					onClick={() => runMutation.mutate(configId)}
					isLoading={runMutation.isPending}
					disabled={isRunning}
				>
					Run Now
				</Button>
			</div>

			<ScoreFilters filters={filters} onChange={handleFiltersChange} />

			{activeJobId ? <JobProgress jobId={activeJobId} configId={configId} onComplete={handleJobComplete} /> : null}

			{isLoading ? (
				<div>
					<Skeleton style={{ height: 72, marginBottom: 12 }} />
					<Skeleton style={{ height: 72, marginBottom: 12 }} />
					<Skeleton style={{ height: 72 }} />
				</div>
			) : allScores.length === 0 ? (
				<div className={css(styleSheet.emptyState)}>
					<p>No scores yet. Run the configuration to start researching contacts.</p>
				</div>
			) : (
				<div className={css(styleSheet.list)}>
					{allScores.map(score => (
						<ScoreCard key={score.id} score={score} configId={configId} showSuppressed={filters.showSuppressed} />
					))}
					{hasNextPage ? (
						<Waypoint onEnter={handleLoadMore}>
							<div className={css(styleSheet.loadingMore)}>
								<LoadingSpinner type='small' />
							</div>
						</Waypoint>
					) : null}
				</div>
			)}
		</>
	);
};
